import { useState, useEffect, useCallback, useMemo } from 'react';
import { financingsApi } from '../../../../services/api/financings';
import { costsApi } from '../../../../services/api/costs';
import {
  FinancingWithLoans,
  FinancingUpdate,
  LoanWithPlan,
  Capex,
  SelectOption,
  ValidationsResponse,
} from '../../../../types';
import { hasFieldChanged } from '../../../../utils/formUtils';

interface UseFinancingDataProps {
  projectId: string;
  /** Callback für Validierungen aus API-Responses → PageWarningsContext */
  onValidationsReceived?: (validations: ValidationsResponse) => void;
}

interface UseFinancingDataReturn {
  // Daten
  financing: FinancingWithLoans | null;
  capex: Capex | null;
  loans: LoanWithPlan[];
  darlehensTypOptions: SelectOption[];

  // Berechnete Werte
  gesamtinvestition: number;
  fkQuote: number;
  eigenkapital: number;
  fremdkapital: number;
  summeDarlehen: number;

  // Status
  isLoading: boolean;
  error: string | null;
  isSaving: boolean;

  // Aktionen
  updateFinancingField: (field: keyof FinancingUpdate, value: number | undefined) => Promise<void>;
  reloadLoans: () => Promise<void>;
  reload: () => Promise<void>;
}

/**
 * Hook für das Laden und Aktualisieren von Finanzierungsdaten.
 *
 * Lädt Financing (inkl. Loans) und CAPEX.
 * Der Tilgungsplan wird separat über useRepaymentPlan in der RepaymentSection geladen,
 * damit Darlehens-Bearbeitungen die restliche Seite nicht blockieren.
 */
export const useFinancingData = ({
  projectId,
  onValidationsReceived,
}: UseFinancingDataProps): UseFinancingDataReturn => {
  const [financing, setFinancing] = useState<FinancingWithLoans | null>(null);
  const [capex, setCapex] = useState<Capex | null>(null);
  const [darlehensTypOptions, setDarlehensTypOptions] = useState<SelectOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Loans aus Financing extrahieren
  const loans = useMemo(() => {
    return financing?.loans || [];
  }, [financing]);

  // Berechnete Werte
  const gesamtinvestition = useMemo(() => {
    return capex?.summe_gesamt || 0;
  }, [capex]);

  const ekQuote = useMemo(() => {
    return financing?.eigenkapitalquote || 0;
  }, [financing]);

  const fkQuote = useMemo(() => {
    return Math.max(0, 100 - ekQuote);
  }, [ekQuote]);

  const eigenkapital = useMemo(() => {
    return gesamtinvestition * (ekQuote / 100);
  }, [gesamtinvestition, ekQuote]);

  const fremdkapital = useMemo(() => {
    return gesamtinvestition * (fkQuote / 100);
  }, [gesamtinvestition, fkQuote]);

  // Summe aller Darlehenshöhen
  const summeDarlehen = useMemo(() => {
    return loans.reduce((sum, loan) => sum + (loan.darlehenshoehe || 0), 0);
  }, [loans]);

  // Lade Finanzierung und CAPEX parallel
  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const [financingData, capexData, typOptions] = await Promise.all([
        financingsApi.getFinancing(projectId),
        costsApi.getCapex(projectId),
        financingsApi.getDarlehensTypOptions(),
      ]);

      setFinancing(financingData);
      setCapex(capexData);
      setDarlehensTypOptions(typOptions ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Laden der Finanzierungsdaten');
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  // Lade nur Financing (inkl. Loans) neu – z.B. nach Darlehens-Mutation
  const reloadLoans = useCallback(async () => {
    try {
      const financingData = await financingsApi.getFinancing(projectId);
      setFinancing(financingData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Neuladen');
    }
  }, [projectId]);

  // Initial laden
  useEffect(() => {
    loadData();
  }, [loadData]);

  /**
   * Finanzierungsfeld aktualisieren und sofort speichern (nur EK-Quote).
   * Wird bei onBlur (Feldverlassen) bzw. Slider-Change aufgerufen.
   */
  const updateFinancingField = useCallback(async (
    field: keyof FinancingUpdate,
    value: number | undefined
  ) => {
    if (!hasFieldChanged(financing, field as string, value)) return;

    const updates: FinancingUpdate = { [field]: value };

    try {
      setIsSaving(true);
      setError(null);

      const response = await financingsApi.updateFinancing(projectId, updates);
      setFinancing(response.data);
      onValidationsReceived?.(response.validations);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  }, [projectId, financing, onValidationsReceived]);

  return {
    // Daten
    financing,
    capex,
    loans,
    darlehensTypOptions,

    // Berechnete Werte
    gesamtinvestition,
    fkQuote,
    eigenkapital,
    fremdkapital,
    summeDarlehen,

    // Status
    isLoading,
    error,
    isSaving,

    // Aktionen
    updateFinancingField,
    reloadLoans,
    reload: loadData,
  };
};
